// Central config. Values come from env/.env.* (see env/.env.dev.sample).
// We read import.meta.env (Vite) but keep the guide's REACT_APP_* names so the
// .env files match the build guide 1:1.

const env = import.meta.env;

export type DataMode = "mock" | "graph";
export type AuthMode = "msal" | "teams";

export const LeaveSystemConfig = {
  /**
   * "mock"  -> in-memory data, app runs standalone in a browser (default).
   * "graph" -> live SharePoint lists via Microsoft Graph.
   */
  dataMode: (env.REACT_APP_DATA_MODE ?? "mock") as DataMode,

  /**
   * How the app acquires a Graph token in graph mode:
   * "msal"  -> browser sign-in via MSAL (default; runs outside Teams).
   * "teams" -> Teams SSO via TeamsFx (for the in-Teams deployment).
   */
  authMode: (env.REACT_APP_AUTH_MODE ?? "msal") as AuthMode,
  clientId: env.REACT_APP_CLIENT_ID ?? "",
  tenantId: env.REACT_APP_TENANT_ID ?? "",

  siteId: env.REACT_APP_SITE_ID ?? "",
  groupId: env.REACT_APP_GROUP_ID ?? "",
  lists: {
    employees: env.REACT_APP_LIST_EMPLOYEES ?? "",
    leaveTypes: env.REACT_APP_LIST_LEAVE_TYPES ?? "",
    leaveBalances: env.REACT_APP_LIST_LEAVE_BALANCES ?? "",
    leaveRequests: env.REACT_APP_LIST_LEAVE_REQUESTS ?? "",
    holidays: env.REACT_APP_LIST_HOLIDAYS ?? "",
  },

  /**
   * Document library (by name) on the leave site where attachments such as
   * medical certificates are uploaded. Needs no extra Graph permission.
   */
  attachmentLibrary: env.REACT_APP_ATTACHMENT_LIBRARY ?? "Medical Certificates",

  /**
   * Copilot Studio "Katie" agent — authenticated embed via the Microsoft 365
   * Agents SDK (@microsoft/agents-copilotstudio-client + botframework-webchat).
   * The bubble reuses this app's MSAL sign-in to get a Power Platform token
   * (scope CopilotStudio.Copilots.Invoke), so Katie answers as the signed-in
   * user and can read the SharePoint leave lists on their behalf.
   *
   * environmentId + schemaName identify the agent (same across environments,
   * so defaulted here; override via env if the agent moves). appClientId /
   * tenantId come from the existing MSAL config above.
   */
  katie: {
    // Katie 5.0 — standard-harness Copilot Studio agent (the GitHub-Copilot-harness
    // Katie 4.0 couldn't use the Direct Line / Agents SDK conversations action).
    environmentId: env.REACT_APP_KATIE_ENV_ID ?? "cec844a7-2382-efc5-9904-0b3516125890",
    schemaName: env.REACT_APP_KATIE_SCHEMA ?? "kti_Katie50",
  },

  /**
   * When true, the Team Calendar reads/writes the M365 *group* calendar via
   * Graph — which needs the admin-consent scopes Group.Read.All +
   * Calendars.ReadWrite.Shared. When false (default), the Team Calendar is
   * derived from approved rows in the LeaveRequests list instead, so the app
   * needs no admin-consent scopes. (The Power Automate flow still writes the
   * group calendar for the Teams native channel-calendar tab either way.)
   */
  useGroupCalendar: (env.REACT_APP_USE_GROUP_CALENDAR ?? "false") === "true",
};

// Scopes a normal user can typically self-consent to.
const BASE_SCOPES = ["User.Read", "User.ReadBasic.All", "Sites.ReadWrite.All"];
// Group-calendar scopes — require TENANT ADMIN consent.
const GROUP_CALENDAR_SCOPES = ["Group.Read.All", "Calendars.ReadWrite.Shared"];

/** The Graph scopes the app requests at sign-in. */
export const GRAPH_SCOPES = LeaveSystemConfig.useGroupCalendar
  ? [...BASE_SCOPES, ...GROUP_CALENDAR_SCOPES]
  : BASE_SCOPES;

/** True when every Graph-required id is present and mode is "graph". */
export function isGraphConfigured(): boolean {
  if (LeaveSystemConfig.dataMode !== "graph") return false;
  const { siteId, lists } = LeaveSystemConfig;
  return Boolean(
    siteId &&
      lists.employees &&
      lists.leaveTypes &&
      lists.leaveBalances &&
      lists.leaveRequests &&
      lists.holidays,
  );
}
